import React, { useCallback, useMemo } from "react";
import { SharedMedia } from "./types";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindGrid from "library/components/_tailwind/grid";
import MessageCenterMessageThumbnails from "../messages-reader-message-thumbnails";
import TailwindTranslatedText from "library/components/_tailwind/translated-text";
import LanguageStore from "library/core/stores/language/LanguageStore";
import ProfileStore from "common/my-page/stores/profile/ProfileStore";
import { inject, observer } from "mobx-react";
import { SharedMediaTypeEnum } from "../stores/messages/enum";

type MCSharedMediaModalListProps = {
  media: Array<SharedMedia>;
  isMCPhase2Active?: boolean;
  showSingleImg?: boolean;
  profileStore?: ProfileStore;
  languageStore?: LanguageStore;
};

const MONTH_I18N_KEYS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

type MonthGroup = {
  key: string;
  monthIndex: number;
  year: number;
  media: Array<SharedMedia>;
};

const MCSharedMediaModalGrid: React.ComponentType<MCSharedMediaModalListProps> =
  ({
    media,
    isMCPhase2Active = false,
    showSingleImg,
    profileStore,
    languageStore,
  }) => {
    const { profile } = profileStore!;
    const { intl } = languageStore!;

    const isSenderCurrentUser = useCallback(
      id => {
        return id === profile?.["user_id"];
      },
      [profile]
    );

    const currentMonthKey = useMemo(() => {
      const today = new Date();
      return `${today.getMonth()}-${today.getFullYear()}`;
    }, []);

    const mediaNormalized = useMemo<Array<MonthGroup>>(() => {
      const groupsByKey = new Map<string, MonthGroup>();
      const orderedKeys: Array<string> = [];

      media.forEach(m => {
        if (!m.created_at) {
          return;
        }
        const date = new Date(m.created_at);
        const monthIndex = date.getMonth();
        const year = date.getFullYear();
        const key = `${monthIndex}-${year}`;

        if (!groupsByKey.has(key)) {
          groupsByKey.set(key, {
            key,
            monthIndex,
            year,
            media: [],
          });
          orderedKeys.push(key);
        }

        groupsByKey.get(key)!.media.push(m);
      });

      return orderedKeys.map(key => groupsByKey.get(key)!);
    }, [media]);

    return (
      <TailwindFlex flexDirection={"flex-col"}>
        {mediaNormalized.map(m => {
          const monthI18nKey = MONTH_I18N_KEYS[m.monthIndex];
          const translatedMonth = monthI18nKey
            ? intl.formatMessage({
                id: `common.months.${monthI18nKey}.long`,
                defaultMessage: monthI18nKey,
              })
            : "";
          return (
            <React.Fragment key={m.key}>
              <TailwindFlex
                className={["MCSharedMediaModalGrid__date"]}
                backgroundColor={"bg-primary-bg-color"}
                position={"sticky"}
                zIndex={"z-20"}
                inset={["top-0"]}
                padding={["pb-4"]}>
                {m.key === currentMonthKey ? (
                  <TailwindTranslatedText
                    descriptor={{
                      id: `message-center.this-month`,
                    }}
                  />
                ) : (
                  <TailwindTranslatedText
                    descriptor={{
                      id: `message-center.media-month-year`,
                      defaultMessage: "{month}, {year}",
                    }}
                    values={{
                      month: translatedMonth,
                      year: m.year,
                    }}
                  />
                )}
              </TailwindFlex>
              <TailwindGrid
                sm={{
                  cols: "grid-cols-2",
                }}
                cols={"grid-cols-4"}
                gap='gap-2'
                padding={["pb-4"]}>
                {m.media.map(md => (
                  <TailwindFlex
                    key={md.id}
                    textColor={
                      isSenderCurrentUser(md.sender)
                        ? "text-white"
                        : "text-main-color"
                    }>
                    <MessageCenterMessageThumbnails
                      isSenderCurrentUser={isSenderCurrentUser(md.sender)}
                      showGalleryVoiceImg={true}
                      backgroundColor={
                        isSenderCurrentUser(md.sender) ? "#84A3BA" : "#ffffff"
                      }
                      showSingleImg={showSingleImg}
                      message={{
                        id: md.id,
                        sender: {
                          id: `${md.sender}`,
                          top_admirer_rank: 0,
                          profile_picture: "",
                          username: "",
                          email: "",
                          first_name: "",
                          last_name: "",
                          is_bounty_member: false,
                          is_fanclub_member: false,
                          is_recent_visitor: false,
                          is_top_admirer: false,
                          is_top_spender: false,
                          is_referral_member: false,
                          can_receive_bulk_message: false,
                          can_receive_message: false,
                          profile_type: "",
                          is_messaging_cs: false,
                        },
                        created_at: md.created_at,
                        status: md.status,
                        attachments_data: [
                          {
                            // Keep signed_url pointing at the actual media and
                            // expose thumbnail_url separately so the thumbnail
                            // renders as an <img>. Cramming the thumbnail into
                            // signed_url made a thumbnail-less video fall back
                            // to the video-poster path and feed an image URL
                            // into a <video> (black square).
                            signed_url: md.signed_url,
                            thumbnail_url:
                              md.attachment_type === SharedMediaTypeEnum.Video
                                ? md.thumbnail_url
                                : undefined,
                            price: md.price,
                            is_purchased: md.is_purchased,
                            attachment_type: md.attachment_type,
                            length: md.length ? parseFloat(md.length) : 0,
                          },
                        ],
                      }}
                      isMCPhase2Active={isMCPhase2Active}
                      disableContextMenu={true}
                    />
                  </TailwindFlex>
                ))}
              </TailwindGrid>
            </React.Fragment>
          );
        })}
      </TailwindFlex>
    );
  };

export default inject(
  "profileStore",
  "languageStore"
)(observer(MCSharedMediaModalGrid));
